import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleArrowUp } from "@fortawesome/pro-solid-svg-icons/faCircleArrowUp";
import { ChangeEvent } from "../../../../client/api";
import PlayerImage from "../../PlayerImage";
import { translate } from "../../../../config/translator";
import { ClavaContext } from "../../../../config/contexts";
import { pathPlayerView } from "../../../../config/routes";
import RoundedRectangle from "../../RoundedRectangle";
import StoreMiniBanner from "../../StoreMiniBanner";
import ScaleFade from "../../Animated/ScaleFade";

type MatchChangesProps = {
  events: ChangeEvent[];
  team1Id: number;
  team2Id: number;
};
const MatchChanges: React.FC<MatchChangesProps> = ({
                                                     events,
                                                     team1Id,
                                                     team2Id
                                                   }) => {
  const { l } = useContext(ClavaContext);
  const navigate = useNavigate();
  const [clickedPlayerId, setClickedPlayerId] = useState<string | null>(null);
  const onPlayerClick = useCallback((playerId: number | undefined, changeId: number, side: "left" | "right") => {
    if (playerId) {
      navigate(pathPlayerView(playerId));
    } else {
      setClickedPlayerId(`${side}-${changeId}`);
    }
  }, [navigate]);
  const changes1 = useMemo(
    () =>
      events
        .filter((e) => e.teamId === team1Id)
        .sort((a, b) => (a.minute ?? 0) - (b.minute ?? 0)),
    [events, team1Id]
  );
  const changes2 = useMemo(
    () =>
      events
        .filter((e) => e.teamId === team2Id)
        .sort((a, b) => (a.minute ?? 0) - (b.minute ?? 0)),
    [events, team2Id]
  );
  return (
    <RoundedRectangle
      className="rounded-xl bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4  w-full"
      hasBorder
      hasTitle
      titleType="upCenter"
      titleKey="changes"
    >
      <div className="flex flex-col items-stretch justify-start tablet-panel-body">
        {changes1.length === 0 && changes2.length === 0 && (
          <span
            className="w-full h-24 flex items-center justify-center text-center text-font-600 dark:text-font-600-dark">
            {translate("noChanges", l)}
          </span>
        )}
        {Array(Math.max(changes1.length, changes2.length))
          .fill(0)
          .map((_, idx) => {
            const left = changes1[idx];
            const right = changes2[idx];
            return (
              <div
                key={`change-row-${left?.id ?? "x"}-${right?.id ?? "x"}`}
                className="grid grid-cols-2 gap-4 py-4"
              >
                <div className="flex flex-col items-center justify-start">
                  {left && (
                    <div className="flex flex-col items-center justify-start text-center">
                      <div className="relative inline-block flex-none mb-2">
                        <ScaleFade isOpen hoverOnContainer className="w-16 h-16">
                          <button type="button" onClick={() => onPlayerClick(left.playerIn?.id, left.id, "left")}
                                  className="w-16 h-16 overflow-hidden rounded-full cursor-pointer">
                            <PlayerImage image={left.playerIn?.photo ?? undefined} width={64}
                                         className="rounded-full" />
                          </button>
                        </ScaleFade>
                        <div className="absolute -top-3 -right-8 flex items-center gap-1">
                          <FontAwesomeIcon
                            icon={faCircleArrowUp}
                            className="w-5 h-5 text-green"
                          />
                          <span className="text-green text-xs">
                            {left.minute ? `${left.minute}'` : ""}
                          </span>
                        </div>
                        {clickedPlayerId === `left-${left.id}` && (
                          <div className="absolute -top-2 left-8 z-10">
                            <StoreMiniBanner compact noBorder message={translate("downloadUnlimited", l)}
                                             onClose={() => setClickedPlayerId(null)} autoHideMs={6000} />
                          </div>
                        )}
                      </div>
                      <span className="font-medium text-font dark:text-font-dark text-center text-xs">
                        {left.playerIn?.familyName} {left.playerIn?.givenName}
                      </span>
                      <span className="text-font-600 dark:text-font-600-dark text-center text-xs">
                        {translate("forWord", l)} {left.player?.familyName}{" "}
                        {left.player?.givenName}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-center justify-start">
                  {right && (
                    <div className="flex flex-col items-center justify-start text-center">
                      <div className="relative inline-block flex-none mb-2">
                        <ScaleFade isOpen hoverOnContainer className="w-16 h-16">
                          <button type="button" onClick={() => onPlayerClick(right.playerIn?.id, right.id, "right")}
                                  className="w-16 h-16 overflow-hidden rounded-full cursor-pointer">
                            <PlayerImage image={right.playerIn?.photo ?? undefined} width={64}
                                         className="rounded-full" />
                          </button>
                        </ScaleFade>
                        <div className="absolute -top-3 -left-8 flex items-center gap-1">
                          <FontAwesomeIcon
                            icon={faCircleArrowUp}
                            className="w-5 h-5 text-green"
                          />
                          <span className="text-green text-xs">
                            {right.minute ? `${right.minute}'` : ""}
                          </span>
                        </div>
                        {clickedPlayerId === `right-${right.id}` && (
                          <div className="absolute -top-2 right-8 z-10">
                            <StoreMiniBanner compact noBorder message={translate("downloadUnlimited", l)}
                                             onClose={() => setClickedPlayerId(null)} autoHideMs={6000} />
                          </div>
                        )}
                      </div>
                      <span className="font-medium text-font dark:text-font-dark text-center text-xs">
                        {right.playerIn?.familyName} {right.playerIn?.givenName}
                      </span>
                      <span className="text-font-600 dark:text-font-600-dark text-center text-xs">
                        {translate("forWord", l)} {right.player?.familyName}{" "}
                        {right.player?.givenName}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </RoundedRectangle>
  );
};

export default MatchChanges;
